import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

export default function Navbar() {
  const [scrolled,     setScrolled]     = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [searchQuery,  setSearchQuery]  = useState('');
  const { count }  = useCart();
  const location   = useLocation();
  const navigate   = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setSearchOpen(false); }, [location]);

  const handleSearch = e => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false); setSearchQuery('');
    }
  };

  const NAV = [['/', 'Home'], ['/shop', 'Collection'], ['/about', 'Our Story'], ['/contact', 'Contact']];

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'rgba(7,7,10,0.96)' : 'rgba(7,7,10,0.0)',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(246,241,231,0.08)' : 'none',
        transition: 'all 0.5s cubic-bezier(0.4,0,0.2,1)',
        width: '100%',
      }}>
        <div style={{ padding: '0 clamp(20px, 6vw, 100px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'baseline', gap: 10, textDecoration: 'none' }}>
            <span style={{ fontFamily: 'var(--font-serif)', fontSize: 38, fontWeight: 300, fontStyle: 'italic', letterSpacing: '-0.02em', color: '#f6f1e7', lineHeight: 1 }}>Sudath</span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, letterSpacing: '0.4em', color: '#d4b06a', textTransform: 'uppercase', fontWeight: 500 }}>Gems</span>
          </Link>

          {/* Desktop Nav */}
          <div className="nav-links" style={{ display: 'flex', gap: 40 }}>
            {NAV.map(([path, label]) => {
              const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
              return (
                <Link key={path} to={path} style={{
                  fontSize: 10.5, letterSpacing: '2.5px', textTransform: 'uppercase',
                  fontWeight: 500, fontFamily: 'var(--font-sans)',
                  color: active ? '#d4b06a' : 'rgba(246,241,231,0.72)',
                  transition: 'color 0.3s ease', textDecoration: 'none',
                  paddingBottom: 2,
                  borderBottom: active ? '1px solid #d4b06a' : '1px solid transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#d4b06a'}
                onMouseLeave={e => e.currentTarget.style.color = active ? '#d4b06a' : 'rgba(246,241,231,0.72)'}
                >{label}</Link>
              );
            })}
          </div>

          {/* Right actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 3vw, 22px)' }}>
            {/* Search toggle */}
            <button onClick={() => setSearchOpen(!searchOpen)}
              style={{ color: 'rgba(246,241,231,0.6)', fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s', padding: 4, display: 'flex' }}
              onMouseEnter={e => e.currentTarget.style.color = '#d4b06a'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,241,231,0.6)'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </button>

            {/* Cart */}
            <Link to="/cart" style={{ position: 'relative', color: 'rgba(246,241,231,0.6)', textDecoration: 'none', transition: 'color 0.2s', padding: 4, display: 'flex' }}
              onMouseEnter={e => e.currentTarget.style.color = '#d4b06a'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,241,231,0.6)'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              {count > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: '#d4b06a', color: '#07070a',
                  width: 16, height: 16, borderRadius: '50%',
                  fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--font-sans)',
                }}>{count}</span>
              )}
            </Link>

            {/* Mobile menu toggle */}
            <button onClick={() => setMenuOpen(!menuOpen)} style={{
              display: 'none', color: '#f6f1e7', cursor: 'pointer', padding: '7px 9px',
              background: 'rgba(7,7,10,0.55)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(246,241,231,0.18)', borderRadius: 8,
            }} className="nav-hamburger">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                {menuOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Search overlay */}
      {searchOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(7,7,10,0.92)', backdropFilter: 'blur(16px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeIn 0.2s ease',
        }} onClick={() => setSearchOpen(false)}>
          <form onSubmit={handleSearch} onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 600, padding: '0 40px' }}>
            <p style={{ fontSize: 11, letterSpacing: '3px', color: '#d4b06a', textTransform: 'uppercase', marginBottom: 20, textAlign: 'center', fontFamily: 'var(--font-sans)' }}>Search the Collection</p>
            <div style={{ position: 'relative' }}>
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Blue sapphire, alexandrite, ruby…"
                style={{
                  width: '100%', padding: '18px 60px 18px 24px',
                  background: 'rgba(246,241,231,0.06)', border: '1px solid rgba(246,241,231,0.15)',
                  borderRadius: 999, color: '#f6f1e7', fontSize: 16,
                  fontFamily: 'var(--font-sans)', outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#d4b06a'}
                onBlur={e => e.target.style.borderColor = 'rgba(246,241,231,0.15)'}
              />
              <button type="submit" style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                width: 42, height: 42, borderRadius: '50%', background: '#d4b06a',
                display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#07070a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </div>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'rgba(246,241,231,0.3)', fontFamily: 'var(--font-sans)' }}>Press Esc to close</p>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: 72, left: 0, right: 0, bottom: 0, zIndex: 900,
          background: 'rgba(7,7,10,0.99)', backdropFilter: 'blur(24px)',
          borderTop: '1px solid rgba(246,241,231,0.07)',
          padding: '32px 24px 48px', animation: 'pageIn 0.25s ease',
          display: 'flex', flexDirection: 'column',
        }}>
          {NAV.map(([path, label]) => (
            <Link key={path} to={path} style={{
              display: 'block', padding: '16px 0',
              fontSize: 13, letterSpacing: '2.5px', textTransform: 'uppercase',
              color: location.pathname === path ? '#d4b06a' : 'rgba(246,241,231,0.7)',
              fontFamily: 'var(--font-sans)', fontWeight: 500,
              borderBottom: '1px solid rgba(246,241,231,0.05)',
            }}>{label}</Link>
          ))}
          <Link to="/cart" style={{
            display: 'block', padding: '16px 0', marginTop: 8,
            fontSize: 13, letterSpacing: '2.5px', textTransform: 'uppercase',
            color: '#d4b06a', fontFamily: 'var(--font-sans)', fontWeight: 500,
          }}>Cart {count > 0 ? `(${count})` : ''}</Link>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from{ opacity:0; } to{ opacity:1; } }
        @keyframes pageIn  { from{ opacity:0; transform:translateY(-8px); } to{ opacity:1; transform:translateY(0); } }
        @media (max-width: 768px) {
          .nav-hamburger { display: flex !important; }
          .nav-links     { display: none !important; }
        }
      `}</style>
    </>
  );
}
